import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import Modal from '../components/Modal';

const KONTEXT_LABELS = {
  kontakt:      { label: 'Kontaktanfrage', icon: '✉', color: '#1565c0', bg: '#e3f2fd' },
  probetraining:{ label: 'Probetraining',  icon: '◎', color: '#6a1b9a', bg: '#f3e5f5' },
  warteliste:   { label: 'Warteliste',     icon: '≡', color: '#e65100', bg: '#fff3e0' },
  schwimmer:    { label: 'Schwimmer',      icon: '👶', color: '#2e7d32', bg: '#e8f5e9' },
  test:         { label: 'Test',           icon: '⚙', color: '#555',    bg: '#f5f5f5' },
};

function fmtDatum(d) {
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmtZeit(d) {
  return new Date(d).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}
function getKW(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 'KW ' + String(1 + Math.round(((date - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7)).padStart(2, '0');
}

function groupByZeit(eintraege) {
  const jahre = {};
  for (const e of eintraege) {
    const d = new Date(e.gesendet_am);
    const jahr = d.getFullYear();
    const monat = d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    const kw = getKW(e.gesendet_am);

    if (!jahre[jahr]) jahre[jahr] = {};
    if (!jahre[jahr][monat]) jahre[jahr][monat] = {};
    if (!jahre[jahr][monat][kw]) jahre[jahr][monat][kw] = [];
    jahre[jahr][monat][kw].push(e);
  }
  return jahre;
}

export default function EmailArchiv() {
  const [eintraege, setEintraege] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [detail, setDetail]       = useState(null);
  const [offeneJahre, setOffeneJahre]   = useState({});
  const [offeneMonate, setOffeneMonate] = useState({});
  const [offeneKW, setOffeneKW]         = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/email-log');
      setEintraege(r.data);
      // Aktuellstes Jahr + Monat + KW automatisch aufklappen
      if (r.data.length > 0) {
        const d = new Date(r.data[0].gesendet_am);
        const jahr = String(d.getFullYear());
        const monat = d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
        const kw = getKW(r.data[0].gesendet_am);
        setOffeneJahre({ [jahr]: true });
        setOffeneMonate({ [`${jahr}_${monat}`]: true });
        setOffeneKW({ [`${jahr}_${monat}_${kw}`]: true });
      }
    } catch {
      setError('E-Mail-Archiv konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleJahr  = j => setOffeneJahre(p  => ({ ...p, [j]: !p[j] }));
  const toggleMonat = k => setOffeneMonate(p => ({ ...p, [k]: !p[k] }));
  const toggleKW    = k => setOffeneKW(p     => ({ ...p, [k]: !p[k] }));

  const gruppiert = groupByZeit(eintraege);
  const jahreKeys = Object.keys(gruppiert).sort((a, b) => b - a);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">
          E-Mail-Archiv{' '}
          <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '1rem' }}>
            ({eintraege.length})
          </span>
        </h2>
        <button className="btn btn-secondary btn-sm" onClick={load}>↻ Aktualisieren</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }} />
        </div>
      ) : eintraege.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">✉</div>
            <div className="empty-state-text">Noch keine E-Mails versendet</div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {jahreKeys.map(jahr => {
            const jahrOffen = !!offeneJahre[jahr];
            const monate = gruppiert[jahr];
            const monatKeys = Object.keys(monate).sort((a, b) =>
              new Date(monate[b][Object.keys(monate[b])[0]][0].gesendet_am) -
              new Date(monate[a][Object.keys(monate[a])[0]][0].gesendet_am)
            );
            const jahrAnzahl = monatKeys.reduce((s, m) =>
              s + Object.values(monate[m]).reduce((s2, kw) => s2 + kw.length, 0), 0);

            return (
              <div key={jahr} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Jahr-Header */}
                <button
                  onClick={() => toggleJahr(jahr)}
                  style={{
                    width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                    background: 'var(--primary)', color: '#fff',
                    padding: '0.875rem 1.25rem',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{jahr}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{
                      background: 'rgba(255,255,255,0.2)', borderRadius: 20,
                      padding: '2px 10px', fontSize: '0.8rem', fontWeight: 600,
                    }}>{jahrAnzahl} E-Mails</span>
                    <span style={{ fontSize: '1.1rem' }}>{jahrOffen ? '▲' : '▼'}</span>
                  </span>
                </button>

                {jahrOffen && (
                  <div style={{ padding: '0.5rem' }}>
                    {monatKeys.map(monat => {
                      const mKey = `${jahr}_${monat}`;
                      const monatOffen = !!offeneMonate[mKey];
                      const kwMap = monate[monat];
                      const kwKeys = Object.keys(kwMap).sort((a, b) => {
                        const numA = parseInt(a.replace('KW ', ''));
                        const numB = parseInt(b.replace('KW ', ''));
                        return numB - numA;
                      });
                      const monatAnzahl = kwKeys.reduce((s, k) => s + kwMap[k].length, 0);

                      return (
                        <div key={mKey} style={{ marginBottom: '0.375rem', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                          {/* Monat-Header */}
                          <button
                            onClick={() => toggleMonat(mKey)}
                            style={{
                              width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                              background: '#f0f4f8', padding: '0.625rem 1rem',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}
                          >
                            <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.9rem' }}>
                              📅 {monat}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{
                                background: 'var(--primary)', color: '#fff', borderRadius: 20,
                                padding: '1px 8px', fontSize: '0.75rem', fontWeight: 600,
                              }}>{monatAnzahl}</span>
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{monatOffen ? '▲' : '▼'}</span>
                            </span>
                          </button>

                          {monatOffen && (
                            <div style={{ padding: '0.375rem 0.5rem' }}>
                              {kwKeys.map(kw => {
                                const kwKey = `${mKey}_${kw}`;
                                const kwOffen = !!offeneKW[kwKey];
                                const emails = kwMap[kw];

                                return (
                                  <div key={kwKey} style={{ marginBottom: '0.25rem', border: '1px solid #e8edf3', borderRadius: 6, overflow: 'hidden' }}>
                                    {/* KW-Header */}
                                    <button
                                      onClick={() => toggleKW(kwKey)}
                                      style={{
                                        width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                                        background: '#fafbfc', padding: '0.5rem 0.875rem',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                      }}
                                    >
                                      <span style={{ fontWeight: 600, color: '#555', fontSize: '0.82rem' }}>
                                        {kw}
                                      </span>
                                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span style={{
                                          background: '#e0e7ef', borderRadius: 20,
                                          padding: '1px 7px', fontSize: '0.72rem', color: '#444',
                                        }}>{emails.length}</span>
                                        <span style={{ color: '#aaa', fontSize: '0.85rem' }}>{kwOffen ? '▲' : '▼'}</span>
                                      </span>
                                    </button>

                                    {kwOffen && (
                                      <div>
                                        {emails.map((e, i) => {
                                          const k = KONTEXT_LABELS[e.kontext_typ] || { label: e.kontext_typ || '–', icon: '✉', color: '#555', bg: '#f5f5f5' };
                                          return (
                                            <div
                                              key={e.id}
                                              onClick={() => setDetail(e)}
                                              style={{
                                                display: 'grid',
                                                gridTemplateColumns: '100px 1fr auto',
                                                gap: '0.5rem',
                                                alignItems: 'center',
                                                padding: '0.625rem 0.875rem',
                                                borderTop: i > 0 ? '1px solid #f0f0f0' : 'none',
                                                cursor: 'pointer',
                                                background: '#fff',
                                                transition: 'background 0.1s',
                                              }}
                                              onMouseEnter={ev => ev.currentTarget.style.background = '#f5f8ff'}
                                              onMouseLeave={ev => ev.currentTarget.style.background = '#fff'}
                                            >
                                              {/* Datum/Zeit */}
                                              <div>
                                                <div style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--primary)' }}>
                                                  {fmtDatum(e.gesendet_am)}
                                                </div>
                                                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                                  {fmtZeit(e.gesendet_am)} Uhr
                                                </div>
                                              </div>

                                              {/* Betreff + Empfänger */}
                                              <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                  {e.betreff}
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                                  An: {e.empfaenger_name ? `${e.empfaenger_name} <${e.empfaenger}>` : e.empfaenger}
                                                  {e.gesendet_von_name && (
                                                    <span style={{ marginLeft: '0.5rem', color: '#aaa' }}>
                                                      · von {e.gesendet_von_name}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>

                                              {/* Kontext-Badge */}
                                              <div style={{ flexShrink: 0 }}>
                                                <span style={{
                                                  padding: '2px 8px', borderRadius: 20, fontSize: '0.72rem',
                                                  fontWeight: 700, background: k.bg, color: k.color,
                                                  whiteSpace: 'nowrap',
                                                }}>
                                                  {k.icon} {k.label}
                                                </span>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Detail-Modal */}
      {detail && (
        <Modal title="E-Mail Details" onClose={() => setDetail(null)} size="md">
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-label">Gesendet am</div>
              <div className="detail-value">
                {fmtDatum(detail.gesendet_am)} um {fmtZeit(detail.gesendet_am)} Uhr
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Von</div>
              <div className="detail-value">{detail.gesendet_von_name || '–'}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">An</div>
              <div className="detail-value">
                {detail.empfaenger_name
                  ? `${detail.empfaenger_name} <${detail.empfaenger}>`
                  : detail.empfaenger}
              </div>
            </div>
            {detail.absender_email && (
              <div className="detail-item">
                <div className="detail-label">Absender</div>
                <div className="detail-value">{detail.absender_email}</div>
              </div>
            )}
            <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
              <div className="detail-label">Betreff</div>
              <div className="detail-value" style={{ fontWeight: 600 }}>{detail.betreff}</div>
            </div>
            {detail.kontext_typ && (
              <div className="detail-item">
                <div className="detail-label">Kontext</div>
                <div className="detail-value">
                  {(() => {
                    const k = KONTEXT_LABELS[detail.kontext_typ] || {};
                    return (
                      <span style={{
                        padding: '2px 8px', borderRadius: 20, fontSize: '0.8rem',
                        fontWeight: 700, background: k.bg || '#f5f5f5', color: k.color || '#555',
                      }}>
                        {k.icon} {k.label || detail.kontext_typ}
                        {detail.kontext_id && ` #${detail.kontext_id}`}
                      </span>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {detail.nachricht && (
            <div style={{ marginTop: '1rem' }}>
              <div className="detail-label" style={{ marginBottom: '0.375rem' }}>Nachricht</div>
              <div style={{
                background: '#f4f7fb', borderRadius: 6, padding: '0.875rem',
                fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.65,
                color: '#333', border: '1px solid var(--border)',
              }}>
                {detail.nachricht}
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => setDetail(null)}>Schließen</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
